import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PortalAuthProvider } from "@/context/PortalAuthContext";
import { LanguageProvider } from "@/context/LanguageContext";

// Public layout + Home are eager-loaded — they're the landing experience and
// must appear instantly. Everything else (Portfolio detail, all Admin screens)
// is code-split so the initial JS payload stays small on first paint.
import PublicLayout from "@/components/public/PublicLayout";
import Home from "@/pages/public/Home";

const Portfolio = lazy(() => import("@/pages/public/Portfolio"));
const ProjectDetail = lazy(() => import("@/pages/public/ProjectDetail"));
const Insights = lazy(() => import("@/pages/public/Insights"));
const InsightDetail = lazy(() => import("@/pages/public/InsightDetail"));
const ApplicationForm = lazy(() => import("@/pages/public/ApplicationForm"));

const AdminLayout = lazy(() => import("@/components/admin/AdminLayout"));
const AdminLogin = lazy(() => import("@/pages/admin/Login"));
const AdminDashboard = lazy(() => import("@/pages/admin/Dashboard"));
const AdminSettings = lazy(() => import("@/pages/admin/Settings"));
const AdminAbout = lazy(() => import("@/pages/admin/About"));
const AdminServices = lazy(() => import("@/pages/admin/Services"));
const AdminProjects = lazy(() => import("@/pages/admin/Projects"));
const AdminProjectEdit = lazy(() => import("@/pages/admin/ProjectEdit"));
const AdminPosts = lazy(() => import("@/pages/admin/Posts"));
const AdminPostEdit = lazy(() => import("@/pages/admin/PostEdit"));
const AdminMedia = lazy(() => import("@/pages/admin/Media"));
const AdminLeads = lazy(() => import("@/pages/admin/Leads"));
const AdminHomepage = lazy(() => import("@/pages/admin/Homepage"));
const AdminProfile = lazy(() => import("@/pages/admin/Profile"));
const AdminUsers = lazy(() => import("@/pages/admin/Users"));
const AdminApplications = lazy(() => import("@/pages/admin/Applications"));
const AdminClients = lazy(() => import("@/pages/admin/Clients"));
const AdminFinance = lazy(() => import("@/pages/admin/Finance"));
const AdminMessages = lazy(() => import("@/pages/admin/Messages"));

const PortalLayout = lazy(() => import("@/components/portal/PortalLayout"));
const PortalLogin = lazy(() => import("@/pages/portal/PortalLogin"));
const PortalRegister = lazy(() => import("@/pages/portal/PortalRegister"));
const PortalDashboard = lazy(() => import("@/pages/portal/PortalDashboard"));
const PortalBriefing = lazy(() => import("@/pages/portal/PortalBriefing"));
const PortalFiles = lazy(() => import("@/pages/portal/PortalFiles"));
const PortalMessages = lazy(() => import("@/pages/portal/PortalMessages"));
const PortalPayments = lazy(() => import("@/pages/portal/PortalPayments"));
const PortalSettings = lazy(() => import("@/pages/portal/PortalSettings"));

function ProtectedRoute({ children, permission }) {
  const { user, can } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-onyx text-gold font-display text-2xl tracking-widest">
        loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;
  if (permission && !can(permission)) return <Navigate to="/admin" replace />;
  return children;
}

// Minimal fallback while a split chunk loads — matches the admin's aesthetic.
const PageFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-paper text-gold-light font-display text-lg tracking-widest">
    loading…
  </div>
);

export default function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <PortalAuthProvider>
          <BrowserRouter>
            <Toaster theme="dark" position="top-right" toastOptions={{ style: { background: "#111", color: "#F5F5F5", border: "1px solid #332D20" } }} />
            <Suspense fallback={<PageFallback />}>
              <Routes>
                {/* Public */}
                <Route element={<PublicLayout />}>
                  <Route path="/" element={<Home />} />
                  <Route path="/portfolio" element={<Portfolio />} />
                  <Route path="/portfolio/:slug" element={<ProjectDetail />} />
                  <Route path="/insights" element={<Insights />} />
                  <Route path="/insights/:slug" element={<InsightDetail />} />
                </Route>
                {/* Public — application funnel (rendered without PublicLayout because it has its own canvas) */}
                <Route path="/start-project" element={<ApplicationForm />} />

                {/* Admin */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <AdminLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<AdminDashboard />} />
                  <Route path="settings" element={<ProtectedRoute permission="settings"><AdminSettings /></ProtectedRoute>} />
                  <Route path="about" element={<ProtectedRoute permission="about"><AdminAbout /></ProtectedRoute>} />
                  <Route path="services" element={<AdminServices />} />
                  <Route path="projects" element={<AdminProjects />} />
                  <Route path="projects/:id" element={<AdminProjectEdit />} />
                  <Route path="projects/new" element={<AdminProjectEdit />} />
                  <Route path="posts" element={<AdminPosts />} />
                  <Route path="posts/:id" element={<AdminPostEdit />} />
                  <Route path="posts/new" element={<AdminPostEdit />} />
                  <Route path="media" element={<AdminMedia />} />
                  <Route path="leads" element={<AdminLeads />} />
                  <Route path="applications" element={<ProtectedRoute permission="portal"><AdminApplications /></ProtectedRoute>} />
                  <Route path="clients" element={<ProtectedRoute permission="portal"><AdminClients /></ProtectedRoute>} />
                  <Route path="finance" element={<ProtectedRoute permission="finance"><AdminFinance /></ProtectedRoute>} />
                  <Route path="messages" element={<ProtectedRoute permission="portal"><AdminMessages /></ProtectedRoute>} />
                  <Route path="homepage" element={<ProtectedRoute permission="homepage"><AdminHomepage /></ProtectedRoute>} />
                  <Route path="profile" element={<AdminProfile />} />
                  <Route path="users" element={<ProtectedRoute permission="users"><AdminUsers /></ProtectedRoute>} />
                </Route>

                {/* Client Portal */}
                <Route path="/portal/login" element={<PortalLogin />} />
                <Route path="/portal/register" element={<PortalRegister />} />
                <Route path="/portal" element={<PortalLayout />}>
                  <Route index element={<PortalDashboard />} />
                  <Route path="briefing" element={<PortalBriefing />} />
                  <Route path="files" element={<PortalFiles />} />
                  <Route path="messages" element={<PortalMessages />} />
                  <Route path="payments" element={<PortalPayments />} />
                  <Route path="settings" element={<PortalSettings />} />
                </Route>

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </PortalAuthProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}
